import React, {useContext, useState, useEffect, useRef, useCallback} from 'react'
import {useParams, useNavigate, useLocation} from 'react-router-dom'
import git, {ReadCommitResult} from 'isomorphic-git'
import FS from "@isomorphic-git/lightning-fs"
import {Menu, MenuItem} from '@mui/material'
import {KeyboardArrowDown} from '@mui/icons-material'

import {BackendContext} from '../../../context/BackendContext'
import {useGitDir} from '../../../hooks/useGitDir'
import {LoadingPageTransition} from '../../../components/App/LoadingPageTransition'
import {CommitDetailsRow} from '../../../components/Repo/CommitDetailsRow'
import {CommitsStatus} from '../../../components/Repo/CommitsStatus'
import {sortCommit, findCommitNumber, groupCommitsByDates, isToday} from './utils'
import styles from "./index.module.scss"

export const CommitHistory = () => {
  const {orbitId, repoId, branch} = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const {fs} = useContext(BackendContext)
  const gitDir = useGitDir(orbitId, repoId)
  const [commits, setCommits] = useState<ReadCommitResult[]>([])
  const [allBranches, setAllBranches] = useState<string[]>([])

  const branchAnchorEl = useRef<HTMLDivElement | null>(null)
  const [branchMenuOpen, setBranchMenuOpen] = useState(false)
  const onClickBranchSelector = useCallback((_: any) => {
    setBranchMenuOpen(true)
  }, [])

  const onCloseBranchSelector = useCallback((_: any) => {
    setBranchMenuOpen(false)
  }, [])
  const onSelectBranch = useCallback((event: any) => {
    const newBranch = event.target.innerText
    const currentPath = location.pathname
    if (currentPath === undefined) {
      return
    }
    const newPath = currentPath.replace(
      new RegExp(`/commits/${branch}`),
      `/commits/${newBranch}`
    )
    navigate(newPath)
    setBranchMenuOpen(false)
  }, [branch, location, navigate])
  const getAllBranches = useCallback(async (
    fs: FS,
    gitDir: string
  ) => {
    const branches = await git.listBranches({
      fs: fs,
      gitdir: gitDir
    })
    setAllBranches(branches)
  }, [])

  useEffect(() => {
    if (!fs || !gitDir) {
      return
    }

    getAllBranches(fs, gitDir).then()
    git.log({fs, gitdir: gitDir, ref: branch}).then(
      commits => setCommits(commits.sort(sortCommit))
    )
  }, [fs, gitDir, setCommits])

  return fs && gitDir ? (
    <div className={styles.Container}>
      <h1 className={styles.Title}>Commit History</h1>
      <div className={styles.HeaderRow}>
        <div className={styles.BranchSelector}
              onClick={onClickBranchSelector}
              ref={branchAnchorEl}
              >
          <div className={styles.CurrentBranch}>
            {branch}
          </div>
          <KeyboardArrowDown/>
        </div>
        <CommitsStatus commits={commits}/>
      </div>
      {Object.entries(groupCommitsByDates(commits)).map(([date, cmts]) => (
        <>
          <h2 className={styles.SubTitle}>
            {isToday(date) ? "Today" : date}
          </h2>
          {cmts.map(cmt => <CommitDetailsRow commit={cmt} commitNumber={findCommitNumber(commits, cmt.oid)} seeDiff/>)}
        </>
      ))}

      <Menu
        id="branch-menu"
        open={branchMenuOpen}
        anchorEl={branchAnchorEl.current}
        onClose={onCloseBranchSelector}
        classes={{paper: styles.BranchMenuPaper, list: styles.BranchMenuList}}
      >
        {
          allBranches.map((b, i) => (
            <MenuItem
              key={b}
              onClick={onSelectBranch}
              classes={{
                root: styles.BranchMenu, 
                gutters: i === allBranches.length - 1 ? styles.LastBranchMenuItem : styles.BranchMenuItem
              }}
            >
              {b}
            </MenuItem>
          ))
        }
      </Menu>
    </div>
  ) :
  <LoadingPageTransition />
}